// Inspecciones (F3a) → Checklist del Anexo 1. Reglas DECIDIDAS POR CLAUDE, por validar con Kevin:
//  - PO-14 (programa anual de inspecciones): desde el lote C lo calcula y escribe prog-checklist.ts (programa anual
//    aprobado y firmado; cada inspección firmada ejecuta su actividad). resultadoPO14 (programaciones activas) queda
//    como traza de ese cálculo; este archivo ya no escribe PO-14.
//  - PO-15 (inspecciones ejecutadas con medidas correctivas): no cumple si hay algún hallazgo vencido; cumple con al menos
//    una inspección firmada en los últimos 12 meses (ventana POR VALIDAR: el Anexo 1 no fija frecuencia); en proceso si
//    hay inspecciones o hallazgos; pendiente sin nada.
//  - PO-23 (uso correcto de EPP verificado in situ): igual que PO-15, pero solo con las inspecciones cuya plantilla
//    alimenta PO-23 (la de EPP o un ítem marcado PO-23) y sus hallazgos.
// No toca el GT-14 (lo alimenta el módulo anterior, inspection-compliance-sync.ts) ni PO-16, PO-17 y PO-18 (Plan de
// Emergencias v2). Estado manual (misma regla que el plan, PE-B2b): un estado marcado a mano (auto_source NULL) nunca se
// sobrescribe; el Checklist muestra "el cálculo automático dice: X" y el botón "Usar cálculo automático". Nunca se toca
// un "No aplica". Solo corre para empresas que usan el motor nuevo (tienen inspecciones o programaciones).

import { supabase } from '../../supabaseClient';
import type { Traza } from '../pe/pe-tipos';
import { esManual, type EstadoGuardadoChecklist, type EstadoItem, type TrazasPlanV2 } from '../plan-emergencias-compliance-sync';
import { plural } from '../plurales';
import { estadoHallazgo, isoLocal, itemsDe, sumarMeses } from './insp-logica';
import type { FilaHallazgo, FilaInspeccion, FilaProgramacion, FotoPlantilla } from './insp-tipos';

export const FUENTE_INSPECCIONES_V2 = 'inspecciones_v2';
export const MODULO_INSPECCIONES_V2 = 'Inspecciones (motor nuevo)';
export const ITEMS_INSPECCIONES_V2 = ['PO-14', 'PO-15', 'PO-23'] as const;
export type ItemInspeccionesV2 = typeof ITEMS_INSPECCIONES_V2[number];
/** Lo que escribe este archivo (PO-14 lo escribe prog-checklist.ts desde el lote C). */
export const ITEMS_ESCRITOS_V2 = ['PO-15', 'PO-23'] as const;

type InspMin = Pick<FilaInspeccion, 'id' | 'status' | 'signed_at' | 'performed_date'> & { template_snapshot: Pick<FotoPlantilla, 'anexo_items' | 'sections'> | null };
type HallMin = Pick<FilaHallazgo, 'inspection_id' | 'status' | 'due_date'>;
type ProgMin = Pick<FilaProgramacion, 'active' | 'end_date'>;

// F3a2: fuente pública (la línea de docs/normativa/anexo1_lista_verificacion_sst.md queda solo como argumento interno).
export const FUENTE_ANEXO1_PUBLICA = 'Anexo 1, Lista de verificación de cumplimiento de obligaciones de seguridad y salud en el trabajo (Ministerio del Trabajo)';
export const FUENTE_ANEXO1 = (_linea: number) => FUENTE_ANEXO1_PUBLICA;
export const NORMA_REGLA_INSPECCIONES = 'Criterio técnico de StorSafe (por validar)';
export const VENTANA_MESES = 12;

/** ¿La inspección cuenta para el ítem? Su plantilla lo lista en anexo_items o tiene un ítem marcado con él. */
export function alimentaItem(i: Pick<InspMin, 'template_snapshot'>, itemId: string): boolean {
  const f = i.template_snapshot;
  if (!f) return false;
  return (f.anexo_items ?? []).includes(itemId) || itemsDe(f as FotoPlantilla).some(it => it.anexo_item === itemId);
}

const traza = (estado: EstadoItem, articulo: string, umbral: number | string | null, valor: number | string | null, unidad: string,
  fuente: string, nota: string): Traza => ({
  metodo: 'inspecciones_v2', norma: NORMA_REGLA_INSPECCIONES, articulo_o_tabla: articulo, umbral, valor, unidad,
  estado: estado === 'cumple' ? 'cumple' : estado === 'no_cumple' ? 'no_cumple' : 'por_validar', fuente_doc: fuente, nota,
});

export interface ResultadoItemInsp { estado: EstadoItem; resumen: string; trazas: Traza[] }

export function resultadoPO14(programaciones: ProgMin[], hoy: Date = new Date()): ResultadoItemInsp {
  const hoyIso = isoLocal(hoy);
  const activas = programaciones.filter(p => p.active && (!p.end_date || p.end_date >= hoyIso)).length;
  const estado: EstadoItem = activas > 0 ? 'en_proceso' : 'pendiente';
  const resumen = activas > 0
    ? `${plural(activas, 'programación de inspección activa', 'programaciones de inspección activas')}. Falta el programa anual firmado (objetivos, alcance y firmas): no se marca Cumple solo.`
    : 'No hay inspecciones programadas.';
  return { estado, resumen, trazas: [traza(estado, 'PO 14: programa anual de inspecciones (cronograma)', 1, activas, 'programaciones', FUENTE_ANEXO1(543), resumen)] };
}

// "que": la palabra en singular y en plural (F3a2: antes se pluralizaba con una "s" y salía "inspeccións").
function resultadoEjecucion(inspecciones: InspMin[], hallazgos: HallMin[], hoy: Date, articulo: string, linea: number,
  que: { uno: string; varios: string }): ResultadoItemInsp {
  const desde = sumarMeses(isoLocal(hoy), -VENTANA_MESES);
  const firmadas = inspecciones.filter(i => i.status === 'completada' && !!i.signed_at && !!i.performed_date && (i.performed_date as string) >= desde).length;
  const vencidos = hallazgos.filter(h => estadoHallazgo(h, hoy) === 'vencido').length;
  const conDatos = inspecciones.some(i => i.status !== 'cancelada') || hallazgos.length > 0;
  const estado: EstadoItem = vencidos > 0 ? 'no_cumple' : firmadas > 0 ? 'cumple' : conDatos ? 'en_proceso' : 'pendiente';
  const resumen = vencidos > 0 ? `${plural(vencidos, 'hallazgo vencido', 'hallazgos vencidos')} sin cerrar.`
    : firmadas > 0 ? `${plural(firmadas, `${que.uno} firmada`, `${que.varios} firmadas`)} en los últimos ${VENTANA_MESES} meses, sin hallazgos vencidos.`
      : conDatos ? `Hay ${que.varios} o hallazgos, pero ninguna firmada en los últimos ${VENTANA_MESES} meses.` : `No hay ${que.varios}.`;
  return {
    estado, resumen, trazas: [
      traza(estado, articulo, 1, firmadas, `${que.varios} firmadas (${VENTANA_MESES} meses, por validar)`, FUENTE_ANEXO1(linea), resumen),
      traza(vencidos > 0 ? 'no_cumple' : 'cumple', 'Hallazgos vencidos (medidas correctivas)', 0, vencidos, 'hallazgos', FUENTE_ANEXO1(linea),
        'Un hallazgo vencido es una medida correctiva no implementada a tiempo.'),
    ],
  };
}

export function resultadoPO15(inspecciones: InspMin[], hallazgos: HallMin[], hoy: Date = new Date()): ResultadoItemInsp {
  return resultadoEjecucion(inspecciones, hallazgos, hoy, 'PO 15: inspecciones ejecutadas in situ y medidas correctivas', 555,
    { uno: 'inspección', varios: 'inspecciones' });
}

export function resultadoPO23(inspecciones: InspMin[], hallazgos: HallMin[], hoy: Date = new Date()): ResultadoItemInsp {
  const deEpp = inspecciones.filter(i => alimentaItem(i, 'PO-23'));
  const ids = new Set(deEpp.map(i => i.id));
  return resultadoEjecucion(deEpp, hallazgos.filter(h => !!h.inspection_id && ids.has(h.inspection_id)), hoy,
    'PO 23: uso correcto de EPP verificado in situ', 660, { uno: 'inspección de EPP', varios: 'inspecciones de EPP' });
}

// ¿Qué se escribe? null = nada. Lo manual (auto_source NULL) nunca se reemplaza; "No aplica" tampoco.
export function decisionInspecciones(existente: EstadoGuardadoChecklist | undefined, deseado: EstadoItem): EstadoItem | null {
  if (existente?.estado === 'no_aplica') return null;
  if (esManual(existente)) return null;
  if (existente?.estado === deseado && existente.auto_source === FUENTE_INSPECCIONES_V2) return null;
  if (!existente && deseado === 'pendiente') return null;
  return deseado;
}

export function resultadosInspecciones(programaciones: ProgMin[], inspecciones: InspMin[], hallazgos: HallMin[], hoy: Date = new Date()): Record<ItemInspeccionesV2, ResultadoItemInsp> {
  return {
    'PO-14': resultadoPO14(programaciones, hoy),
    'PO-15': resultadoPO15(inspecciones, hallazgos, hoy),
    'PO-23': resultadoPO23(inspecciones, hallazgos, hoy),
  };
}

export async function syncInspeccionesV2Compliance(companyId: string, hoy: Date = new Date()): Promise<TrazasPlanV2> {
  if (!companyId) return {};
  const [{ data: progs, error: e1 }, { data: insps, error: e2 }, { data: halls, error: e3 }, { data: existentes }] = await Promise.all([
    supabase.from('insp_schedules').select('active, end_date').eq('company_id', companyId),
    supabase.from('insp_inspections').select('id, status, signed_at, performed_date, template_snapshot').eq('company_id', companyId),
    supabase.from('insp_findings').select('inspection_id, status, due_date').eq('company_id', companyId),
    supabase.from('checklist_item_estados').select('item_id, estado, auto_source').eq('company_id', companyId).in('item_id', [...ITEMS_ESCRITOS_V2]),
  ]);
  // Sin M41 en la base (o sin permiso) no se toca nada.
  if (e1 || e2 || e3) return {};
  const p = (progs ?? []) as ProgMin[], i = (insps ?? []) as InspMin[], h = (halls ?? []) as HallMin[];
  if (p.length === 0 && i.length === 0) return {};
  const res = resultadosInspecciones(p, i, h, hoy);
  const porItem = new Map(((existentes ?? []) as EstadoGuardadoChecklist[]).map(e => [e.item_id, e]));
  const ahora = new Date().toISOString();
  const filas = ITEMS_ESCRITOS_V2.flatMap(item => {
    const d = decisionInspecciones(porItem.get(item), res[item].estado);
    return d ? [{ company_id: companyId, item_id: item, estado: d, auto_source: FUENTE_INSPECCIONES_V2, updated_at: ahora }] : [];
  });
  if (filas.length) {
    const { error } = await supabase.from('checklist_item_estados').upsert(filas, { onConflict: 'company_id,item_id' });
    if (error) console.error('syncInspeccionesV2Compliance', error);
  }
  return Object.fromEntries(ITEMS_ESCRITOS_V2.map(item => {
    const previo = porItem.get(item);
    const manual = esManual(previo);
    return [item, { ...res[item], manualRespetado: manual, estadoManual: manual ? previo!.estado : null, modulo: MODULO_INSPECCIONES_V2, fuente: FUENTE_INSPECCIONES_V2 }];
  }));
}

// "Usar cálculo automático": quita el estado manual y escribe el cálculo de Inspecciones. Nunca sobre "No aplica".
export async function aplicarCalculoInspecciones(companyId: string, itemId: string, estado: EstadoItem): Promise<void> {
  const { data, error } = await supabase.from('checklist_item_estados').select('estado').eq('company_id', companyId).eq('item_id', itemId).maybeSingle();
  if (error) throw new Error(`No se pudo leer el estado de ${itemId}: ${error.message}`);
  if ((data as { estado: string } | null)?.estado === 'no_aplica') throw new Error(`${itemId} está marcado como "No aplica": no se cambia.`);
  const { error: e2 } = await supabase.from('checklist_item_estados').upsert(
    { company_id: companyId, item_id: itemId, estado, auto_source: FUENTE_INSPECCIONES_V2, updated_at: new Date().toISOString() },
    { onConflict: 'company_id,item_id' });
  if (e2) throw new Error(e2.code === '42501' ? 'No tienes permiso para cambiar el Checklist.' : `No se pudo aplicar el cálculo en ${itemId}: ${e2.message}`);
}
